import { useState } from "react";
import { useToast } from "@chakra-ui/react";

/**
 * Custom hook for managing previous booking requests
 * Handles fetching, searching, filtering, and pagination
 */
export default function usePreviousRequests() {
  const toast = useToast();
  const [previousRequests, setPreviousRequests] = useState([]);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [previousRequestsSearch, setPreviousRequestsSearch] = useState({
    address: "",
    service: "",
    dateFrom: "",
    dateTo: "",
  });
  const [previousRequestsPage, setPreviousRequestsPage] = useState(1);
  const previousRequestsPerPage = 10;

  // Fetch previous booking requests
  const fetchPreviousRequests = async () => {
    setLoadingPrevious(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch("/api/last_minute_bookings", {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      const data = await response.json();
      if (data.success) {
        setPreviousRequests(data.booking_requests);
      }
    } catch (error) {
      console.error("Error fetching previous requests:", error);
      toast({
        title: "Error",
        description: "Failed to load previous requests",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingPrevious(false);
    }
  };

  // Filter requests based on search criteria
  const filterRequests = (requests) => {
    return requests.filter((request) => {
      const requestDate = new Date(request.created_at);

      const matchesAddress = !previousRequestsSearch.address ||
        request.address.toLowerCase().includes(previousRequestsSearch.address.toLowerCase());

      const matchesService = !previousRequestsSearch.service ||
        (request.product &&
          request.product.name.toLowerCase().includes(previousRequestsSearch.service.toLowerCase()));

      const matchesDateFrom = !previousRequestsSearch.dateFrom ||
        requestDate >= new Date(previousRequestsSearch.dateFrom);

      const matchesDateTo = !previousRequestsSearch.dateTo ||
        requestDate <= new Date(previousRequestsSearch.dateTo + "T23:59:59");

      return matchesAddress && matchesService && matchesDateFrom && matchesDateTo;
    });
  };

  // Get paginated results
  const paginateRequests = (filteredRequests) => {
    const startIndex = (previousRequestsPage - 1) * previousRequestsPerPage;
    return filteredRequests.slice(startIndex, startIndex + previousRequestsPerPage);
  };

  // Get filtered and paginated requests
  const getDisplayedRequests = () => {
    const filtered = filterRequests(previousRequests);
    return {
      filtered,
      paginated: paginateRequests(filtered),
      totalPages: Math.ceil(filtered.length / previousRequestsPerPage),
    };
  };

  // Clear all search filters
  const clearSearchFilters = () => {
    setPreviousRequestsSearch({
      address: "",
      service: "",
      dateFrom: "",
      dateTo: "",
    });
    setPreviousRequestsPage(1);
  };

  // Update a specific search field
  const updateSearchField = (field, value) => {
    setPreviousRequestsSearch({
      ...previousRequestsSearch,
      [field]: value,
    });
    setPreviousRequestsPage(1); // Reset to first page on search
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return previousRequestsSearch.address ||
      previousRequestsSearch.service ||
      previousRequestsSearch.dateFrom ||
      previousRequestsSearch.dateTo;
  };

  return {
    previousRequests,
    loadingPrevious,
    fetchPreviousRequests,

    // Search
    previousRequestsSearch,
    setPreviousRequestsSearch,
    updateSearchField,
    clearSearchFilters,
    hasActiveFilters: hasActiveFilters(),

    // Pagination
    previousRequestsPage,
    setPreviousRequestsPage,
    previousRequestsPerPage,

    // Results
    getDisplayedRequests,
  };
}
